import { Box } from '@mui/material';
import type { PublicAd, PublicPortadaSlot } from '@ahora-patagonia/contracts';
import { EspacioPublicitario } from '../../../shared/ads/EspacioPublicitario.js';
import { NotaPrincipal, NotaSecundaria } from './PortadaCeldas.js';

export interface PortadaAProps {
  slots: PublicPortadaSlot[];
  /** El "pie de portada" (≥1200): llena el aire bajo la posición 1. Sin campaña, nada. */
  adPieDePortada: PublicAd | undefined;
}

/**
 * Maqueta A — la portada curada de siempre: 6 posiciones, la 1 dominante. 1 columna en 390,
 * 2 en 768, y en 1440 la 1 ocupa dos columnas con la 2 y la 3 apiladas al costado y la
 * 4-6 cerrando en una fila de tres (handoff §1.2).
 *
 * **Los huecos son huecos**: un slot sin nota deja su celda vacía y no se autocompleta —
 * la portada es una decisión editorial, no un relleno.
 *
 * Es la única maqueta que vive **dentro** de la columna principal, así que es la única en la que
 * Última hora arranca a la misma altura que la portada. B y C corren el frente por encima de las
 * dos columnas (ver `HomeView`).
 */
export function PortadaA({ slots, adPieDePortada }: PortadaAProps) {
  const principal = slots.find((s) => s.position === 1)?.nota ?? null;
  const secundarios = slots.filter((s) => s.position >= 2);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
        gap: 2.5,
      }}
    >
      <Box sx={{ gridColumn: { xs: 'auto', sm: '1 / -1', lg: 'span 2' } }}>
        {principal && <NotaPrincipal nota={principal} />}
        {/* Pie de portada, el mejor espacio de la home (bajo la nota principal, arriba del
            pliegue). Se dibuja solo si hay campaña: el `mt` va acá y no en un envoltorio
            fijo para que sin aviso la celda termine en la firma, sin margen fantasma.
            Existe **solo en A**: nace del aire que deja la principal a dos filas contra la
            columna 2+3 apilada, y en B y C ese aire no existe. */}
        {adPieDePortada && (
          <Box sx={{ display: { xs: 'none', lg: 'block' }, mt: 2.5 }}>
            <EspacioPublicitario ubicacion="IN_FEED" ad={adPieDePortada} />
          </Box>
        )}
      </Box>

      {/* Posiciones 2 y 3, apiladas al costado de la principal en escritorio. */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {secundarios.slice(0, 2).map((slot) => (
          <Box key={slot.position}>
            {slot.nota && <NotaSecundaria nota={slot.nota} grande />}
          </Box>
        ))}
      </Box>

      {/* Posiciones 4-6: cierran en una fila de tres en escritorio. */}
      {secundarios.slice(2).map((slot) => (
        <Box key={slot.position}>{slot.nota && <NotaSecundaria nota={slot.nota} />}</Box>
      ))}
    </Box>
  );
}
